import SEO from "@/components/SEO";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/pages/home/components/Footer";
import PdfViewer from "@/components/PdfViewer";

interface LibraryItem {
  id: number;
  product_id: number;
  purchased_at: string;
  product_name?: string;
  product_description?: string;
  pdf_url?: string;
  artista?: string;
  album?: string;
  musica?: string;
}

export default function MinhaContaPage() {
  const navigate = useNavigate();
  const { user, profile, isAdmin } = useAuth();
  const [itens, setItens] = useState<LibraryItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [pdfAberto, setPdfAberto] = useState<{ url: string; titulo: string } | null>(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!user) return;
    async function carregarTudo() {
      setCarregando(true);
      await syncPaidOrders();
      await carregarBiblioteca();
      setCarregando(false);
    }
    carregarTudo();
  }, [user]);

  async function carregarBiblioteca() {
    const { data } = await supabase
      .from("user_library")
      .select("*, product_items(name, description, id)")
      .eq("user_id", user.id)
      .order("purchased_at", { ascending: false });

    if (data) {
      const formatado: LibraryItem[] = data.map((item: any) => ({
        id: item.id,
        product_id: item.product_id,
        purchased_at: item.purchased_at,
        product_name: item.product_items?.name || "Partitura",
        product_description: item.product_items?.description || "",
        pdf_url: item.pdf_url || "",
        artista: item.metadata?.artista || "",
        album: item.metadata?.album || "",
        musica: item.metadata?.musica || "",
      }));
      setItens(formatado);
    }
  }

  async function syncPaidOrders() {
    setSyncing(true);
    try {
      const { data: orders } = await supabase
        .from("order_headers")
        .select("id, status")
        .eq("customer_id", user.id)
        .eq("status", "paid");

      if (!orders || orders.length === 0) {
        setSyncing(false);
        return;
      }

      const orderIds = orders.map((o: any) => o.id);
      const { data: orderItems } = await supabase
        .from("order_items")
        .select("order_id, product_id, product_name")
        .in("order_id", orderIds);

      if (!orderItems || orderItems.length === 0) {
        setSyncing(false);
        return;
      }

      const { data: existingLib } = await supabase
        .from("user_library")
        .select("product_id, order_id")
        .eq("user_id", user.id)
        .in("order_id", orderIds);

      const existingSet = new Set(
        (existingLib || []).map((l: any) => `${l.product_id}-${l.order_id}`)
      );

      // Buscar pdf_urls
      const productIds = orderItems.map((i: any) => Number(i.product_id)).filter(Boolean);
      const { data: assets } = await supabase
        .from("digital_assets")
        .select("product_id, pdf_url")
        .in("product_id", productIds);
      const assetMap = new Map((assets || []).map((a: any) => [a.product_id, a.pdf_url]));

      const toInsert = orderItems
        .filter((item: any) => !existingSet.has(`${item.product_id}-${item.order_id}`))
        .map((item: any) => ({
          user_id: user.id,
          product_id: Number(item.product_id),
          order_id: item.order_id,
          purchased_at: new Date().toISOString(),
          pdf_url: assetMap.get(Number(item.product_id)) || null,
          metadata: { product_name: item.product_name, auto_sync: true },
        }));

      if (toInsert.length > 0) {
        await supabase.from("user_library").insert(toInsert);
      }
    } catch (e) {
      // Silently fail — não bloqueia a UX
    } finally {
      setSyncing(false);
    }
  }

  return (
    <>
      <SEO
        title="Minhas Partituras"
        description="Acesse todas as partituras que você adquiriu na GodoyProd. Visualize em tela cheia diretamente no navegador ou app."
        keywords="minhas partituras, biblioteca pessoal, GodoyProd, visualizar PDF"
        noindex
        canonicalPath="/minha-conta"
      />
      <div className="min-h-screen bg-godoy-black">
        <Navbar />

        <section className="pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="w-full px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <span className="text-godoy-gold text-sm font-semibold tracking-widest uppercase">
                Minha Área
              </span>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-godoy-cream mt-3">
                Minhas Partituras
              </h1>
              <p className="text-godoy-lightgray mt-3 max-w-xl">
                Acesse todas as partituras que você adquiriu. Visualize em tela cheia
                sem precisar baixar — leia diretamente no navegador ou app.
              </p>
              {syncing && (
                <p className="text-godoy-gold/70 text-xs mt-2 flex items-center gap-1.5">
                  <span className="w-3 h-3 border border-godoy-gold border-t-transparent rounded-full animate-spin" />
                  Sincronizando compras recentes...
                </p>
              )}
              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <Link
                  to="/meu-perfil"
                  className="flex items-center gap-2 px-4 py-2 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-sm text-godoy-cream hover:border-godoy-gold/40 hover:text-godoy-gold transition-all"
                >
                  <i className="ri-user-settings-line" />
                  Meu Perfil
                </Link>
                <Link
                  to="/minha-playlist"
                  className="flex items-center gap-2 px-4 py-2 bg-godoy-charcoal/60 border border-godoy-gray/30 rounded-md text-sm text-godoy-cream hover:border-godoy-gold/40 hover:text-godoy-gold transition-all"
                >
                  <i className="ri-play-list-line" />
                  Minha Playlist
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin/partituras"
                    className="flex items-center gap-2 px-4 py-2 bg-godoy-gold/10 border border-godoy-gold/40 rounded-md text-sm text-godoy-gold hover:bg-godoy-gold/20 transition-all"
                  >
                    <i className="ri-shield-user-line" />
                    Painel Admin
                  </Link>
                )}
                <div className="w-10 h-10 rounded-full bg-godoy-gold/10 flex items-center justify-center overflow-hidden">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <i className="ri-user-line text-godoy-gold" />
                  )}
                </div>
                <div>
                  <p className="text-godoy-cream font-medium text-sm">{profile?.nome || "Cliente"}</p>
                  <p className="text-godoy-lightgray text-xs">{user?.email}</p>
                </div>
              </div>
            </div>

            {carregando ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-godoy-gold border-t-transparent rounded-full animate-spin" />
              </div>
            ) : itens.length === 0 ? (
              <div className="text-center py-20 bg-godoy-charcoal/40 border border-godoy-gray/20 rounded-lg">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-godoy-gold/10 flex items-center justify-center">
                  <i className="ri-music-2-line text-2xl text-godoy-gold" />
                </div>
                <h3 className="font-serif text-xl text-godoy-cream mb-2">
                  Nenhuma partitura ainda
                </h3>
                <p className="text-godoy-lightgray text-sm max-w-md mx-auto mb-6">
                  Você ainda não comprou nenhuma partitura. Explore a biblioteca e
                  adquira as partituras dos seus artistas favoritos.
                </p>
                <Link
                  to="/biblioteca"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm"
                >
                  <i className="ri-book-open-line" />
                  Explorar Biblioteca
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {itens.map((item) => (
                  <div
                    key={item.id}
                    className="bg-godoy-charcoal/60 border border-godoy-gray/20 rounded-lg p-6 hover:border-godoy-gold/40 transition-all group"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-godoy-gold/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-music-line text-xl text-godoy-gold" />
                      </div>
                      <span className="text-xs text-godoy-lightgray">
                        {new Date(item.purchased_at).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-godoy-cream mb-1">
                      {item.product_name}
                    </h3>
                    {item.artista && (
                      <p className="text-godoy-lightgray text-sm mb-3">
                        {item.artista} {item.album ? `· ${item.album}` : ""}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-4">
                      {item.pdf_url ? (
                        <button
                          onClick={() =>
                            setPdfAberto({
                              url: item.pdf_url || "",
                              titulo: item.product_name || "Partitura",
                            })
                          }
                          className="flex-1 px-4 py-2.5 bg-godoy-gold text-godoy-black font-semibold rounded-md hover:bg-godoy-goldlight transition-all text-sm text-center whitespace-nowrap"
                        >
                          <i className="ri-eye-line mr-1.5" />
                          Visualizar
                        </button>
                      ) : (
                        <span className="flex-1 px-4 py-2.5 bg-godoy-gray/30 text-godoy-lightgray rounded-md text-sm text-center cursor-not-allowed">
                          PDF em breve
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        <Footer />

        {pdfAberto && (
          <PdfViewer
            url={pdfAberto.url}
            title={pdfAberto.titulo}
            onClose={() => setPdfAberto(null)}
          />
        )}
      </div>
    </>
  );
}